import { menuItemClicked } from '@/Analytics/Segment';
import Carousel from '@/Components/Carousel';
import { PageType } from '@/Components/HeroBanner/HeroTypes';
import MovieHeroBanner from '@/Components/HeroBanner/MovieHeroBanner';
import ShowHeroBanner from '@/Components/HeroBanner/ShowHeroBanner';
import ErrorMessage from '@/Components/Message';
import usePinValidationModal from '@/Components/Modal/usePinValidationModal';
import RailWrapper from '@/Components/Rail/RailWrapper';
import useIsLoaded from '@/Hooks/useIsLoaded';
import useIsNewHistory from '@/Hooks/useIsNewHistory';
import useMedia from '@/Hooks/useMedia';
import usePagination from '@/Hooks/usePagination';
import { ROOT_SECTIONS_QUERY, RootSectionsData } from '@/Hooks/useRootSections';
import Preloader from '@/Layouts/containers/Preloader';
import { Section } from '@/lib/graph-ql';
import { actions, selectors } from '@/Store';
import { getImageType } from '@/Utils/VideoImage';
import { useQuery } from '@apollo/client';
import { withTransaction } from '@elastic/apm-rum-react';
import { AppId, Movie } from '@skytvnz/sky-app-store/lib/types/graph-ql';
import { isEmpty, isNil } from 'ramda';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import styles from './styles.module.scss';

export enum ClickSource {
  Header = 'header',
  RailTile = 'rail_tile',
}
export interface StateType {
  pageName: string;
  sectionId: string;
  clickSource: string;
}
const Home: React.FC = () => {
  const dispatch = useDispatch();
  const isNewHistory = useIsNewHistory();
  const location = useLocation<StateType>();
  const isLoading = useSelector(selectors.home.isLoading);
  const heroes = useSelector(selectors.home.heroes);
  const LastPaginatedRail = useRef({ id: '', index: 0 });
  const heroesDuration: number = useSelector(selectors.home.heroesDuration);
  const rails = useSelector(selectors.home.rails);
  const { isMediaM } = useMedia();
  const { openPinModal } = usePinValidationModal();

  // Ref to track first load
  const isFirstLoad = useRef(true);
  const { railPageSize } = usePagination();
  const [apiIsLoading, setApiIsLoading] = useState(false);

  const isLoaded = useIsLoaded(isLoading);
  // If there are already some Rails or Hero, not show loading
  const isContentLoading = isNil(heroes) && (isNil(rails) || isEmpty(rails)) && isLoading;
  const isContentLoadingError = isNil(heroes) && (isNil(rails) || isEmpty(rails)) && isLoaded;
  const { data: rootSectionsData } = useQuery<RootSectionsData<Section[]>>(ROOT_SECTIONS_QUERY);

  const imageType = getImageType(PageType.Home, isMediaM);

  useEffect(() => {
    if (
      isFirstLoad.current &&
      rootSectionsData?.rootSections &&
      Array.isArray(rootSectionsData.rootSections)
    ) {
      setApiIsLoading(true);
      const path = location.pathname;
      const currentPage = rootSectionsData?.rootSections.find(
        ({ home }) => path === home?.path,
      ) as Section;
      dispatch(
        actions.home.fetchContent({
          initialRailSize: railPageSize.current,
          reloadRailId: isNewHistory ? undefined : LastPaginatedRail.id,
          sectionId: currentPage.id,
          appId: AppId.SkyGoWeb,
        }),
      ).finally(() => {
        setApiIsLoading(false);
        isFirstLoad.current = false;
      });
    }
  }, [dispatch, location, isNewHistory, railPageSize, rootSectionsData]);

  useEffect(() => {
    if (location?.state?.clickSource === ClickSource.Header) {
      menuItemClicked({ menu_item: location?.state?.pageName });
    }
  }, [location]);

  return (
    <Preloader isLoading={apiIsLoading || isContentLoading}>
      {isContentLoadingError ? (
        <ErrorMessage
          buttonText="Reload"
          onButtonClick={() => {
            window.location.reload();
          }}
        />
      ) : (
        <>
          {heroes && (
            <Carousel className={styles.heroBanner} duration={heroesDuration}>
              {heroes.map((hero, index) => {
                const image = hero?.[imageType].uri || '';
                return hero.__typename === 'Show' ? (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={`show-hero-${hero.id}-${index}`}>
                    <ShowHeroBanner
                      indexOnPage={1}
                      show={hero}
                      image={image}
                      imageType={imageType}
                      pageType={PageType.Home}
                      openPinModal={openPinModal}
                    />
                  </span>
                ) : (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={`movie-hero-${hero.id}-${index}`}>
                    <MovieHeroBanner
                      indexOnPage={1}
                      movie={hero as Movie}
                      image={image}
                      imageType={imageType}
                      pageType={PageType.Home}
                    />
                  </span>
                );
              })}
            </Carousel>
          )}
          <RailWrapper
            indexOnPage={2}
            rails={rails}
            LastPaginatedRail={LastPaginatedRail}
            isNewHistory={isNewHistory}
          />
        </>
      )}
    </Preloader>
  );
};

export default withTransaction('Home', 'component')(Home);
